import { DEMO_PROJECT } from './demo-project';
import type { GgxTourDefinition } from './tour-types';

export const HOWTO_13: GgxTourDefinition = {
    key: '[HOWTO-13] Post-Production Automated Monitoring',
    route: '/home',
    project: DEMO_PROJECT,
    estimatedMinutes: 5,
    prerequisite: {
        target: '[test-id="home-page-card-Monitor & Track"]',
        message: 'Open the home page, then start this tour.',
    },
    intro: {
        headline: 'Keep an eye on it after launch',
        subtitle:
            'Once a pipeline is live it keeps answering real questions, and the world keeps ' +
            'changing around it. Answers that were fine last quarter can slowly drift, and ' +
            'nobody notices until a customer does. Monitoring is how you notice first. It runs ' +
            'the same standardized reports you already trust against production data on a ' +
            'schedule, and shows you the trend. In this tour you will open one monitoring ' +
            'task, see what it tracks, watch how it has performed over time, and look inside a ' +
            'single run.',
    },
    outro: {
        title: "That's a wrap!",
        subtitle:
            'You opened Customer Intent Classification Monitoring and saw the three views that ' +
            'matter: the Details that say what is measured and what data it needs, the Overview ' +
            'that shows the trend against its thresholds and lets you run any month, and the ' +
            'Dashboard that explains a single run in full. Monitoring is built from the same ' +
            'approved reports you use in evaluations, so the number you see in production ' +
            'means the same thing as the number you saw in testing. When the trend dips, the ' +
            'next step is human review: Post-Production Human Labeling shows how people label ' +
            'production data so the model can be corrected and retrained.',
        learned: [
            '**Details** say what is measured and what data it needs.',
            '**Overview** shows the trend against thresholds, month by month.',
            '**Dashboard** explains a single run in full.',
        ],
    },
    chapters: [
        {
            name: 'Find the task',
            blurb: 'From the home page to one monitoring task',
            icon: 'map',
            steps: [2, 3],
        },
        {
            name: 'What is measured',
            blurb: 'The pipeline, the metrics and the data it needs',
            icon: 'list',
            steps: [4],
        },
        {
            name: 'Watch it over time',
            blurb: 'Graph view, calendar view and running a month',
            icon: 'pulse',
            steps: [5, 6, 7, 8],
        },
        {
            name: 'Dig into a run',
            blurb: 'The dashboard behind one data point',
            icon: 'grid',
            steps: [9, 10],
        },
    ],
    steps: [
        {
            target: '[test-id="home-page-card-Monitor & Track"] [test-id="launch-button"]',
            title: 'Where production gets watched',
            whatHappens: 'the module opens on the Monitoring page, listing every monitoring task.',
            icon: 'map',
            description:
                '<strong>Monitor & Track</strong> is the module for everything that happens ' +
                'after a pipeline goes live: automated monitoring, and the annotation queues ' +
                'where people review production data. Click <strong>Launch</strong> on this ' +
                'card.',
            side: 'bottom',
            align: 'center',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Launch',
        },
        {
            target: '[test-id^="grid-row-link-Customer Intent Classification Monitoring"]',
            targetText: 'Customer Intent Classification Monitoring',
            climbToRow: true,
            scrollIntoView: true,
            waitForPresent: '[test-id^="Monitoring-row-"]',
            title: 'One task per thing you want to watch',
            whatHappens: 'the task opens on its Details tab.',
            icon: 'search',
            description:
                'Each row is a monitoring task: one pipeline, one set of metrics, one ' +
                'schedule. You can have several for the same pipeline, say one for accuracy and ' +
                'one for a segment of customers you care about. We will open ' +
                '<strong>Customer Intent Classification Monitoring</strong>, which watches how ' +
                'well the intent classifier understands customers in production. Click the row.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Customer Intent Classification Monitoring',
        },
        {
            target: '[test-id="description"]',
            climbTo: '.card',
            frame: true,
            scrollIntoView: true,
            title: 'What this task measures',
            whatHappens: 'next, the tab that shows performance over time.',
            icon: 'list',
            description:
                'The <strong>Customer Intent Classification Pipeline</strong> sorts each ' +
                'customer message into an intent. Because it is a classifier, the task tracks ' +
                'accuracy, precision, recall and F1, calculated against intents that people ' +
                'have confirmed. Below the basics you will find the <strong>input ' +
                'columns</strong> the task needs: <strong>ground_truth</strong>, the correct ' +
                'intent from annotators, and <strong>threshold</strong>, the accuracy level it ' +
                'must stay above. Every task is defined this way, so it is clear what is ' +
                'measured and what data it runs on. Click Continue.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="tab-item-Overview"]',
            scrollIntoView: true,
            title: 'Now, how is it doing?',
            whatHappens: 'the Overview opens on the Graph View.',
            icon: 'pulse',
            description:
                'Knowing what is measured is half the story. <strong>Overview</strong> shows ' +
                'the results of every run, first as a trend line and then as a calendar. Click ' +
                '<strong>Overview</strong>.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Overview',
        },
        {
            target: '[test-id="monitoring-overview-graph-date-range"]',
            climbTo: '.card',
            frame: true,
            scrollIntoView: true,
            title: 'The trend, with its guard rails',
            whatHappens: 'next, the same runs laid out month by month.',
            icon: 'pulse',
            description:
                'In <strong>Graph View</strong> each line is one metric over time, and the ' +
                'dotted lines are the thresholds the task must stay inside. A slow slide or a ' +
                'sudden dip stands out at a glance. Click any data point to open that run and ' +
                'find out why: missed intents, a change in what customers are asking, or ' +
                'something else. Need a metric that is specific to your business? Write it as a ' +
                'report and the task can track it too. Click Continue.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="monitoring-overview-view-toggle"] p-togglebutton',
            targetText: 'Calendar View',
            title: 'Switch to the calendar',
            whatHappens: 'the runs are laid out as one tile per month.',
            icon: 'clock',
            description:
                'The graph is best for spotting a trend. The calendar is best for answering ' +
                '"did we run this in March, and how did it go?". Click <strong>Calendar ' +
                'View</strong>.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Calendar View',
        },
        {
            target: '[test-id="monitoring-overview-calendar-year"]',
            climbTo: '.card',
            frame: true,
            scrollIntoView: true,
            title: 'A month at a time',
            whatHappens: 'next, the dashboard behind one run.',
            icon: 'clock',
            description:
                'Each tile is one month. When a run happened, the tile shows its metrics and ' +
                'whether the job succeeded. When it did not, <strong>Run Job</strong> runs the ' +
                'task for that month right from the tile, so a gap in the record is one click to ' +
                'fill. Use the year picker to look back further. Together, the graph gives you ' +
                'the trend and the calendar gives you month-by-month traceability. Click ' +
                'Continue.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'next'],
        },
        {
            target: '[test-id="tab-item-Dashboard"]',
            scrollIntoView: true,
            title: 'Look inside a run',
            whatHappens: 'the Dashboard opens on the most recent run.',
            icon: 'grid',
            description:
                'Any data point on the graph and any month on the calendar opens a run, and ' +
                '<strong>Jobs</strong> lists all of them. <strong>Dashboard</strong> is the ' +
                'shortcut to the latest one. Click <strong>Dashboard</strong>.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Dashboard',
        },
        {
            target: 'cor-job-dashboard [test-id^="jobtab-panel-"].p-tabpanel-active',
            frame: true,
            scrollIntoView: true,
            title: 'The same report, on production data',
            whatHappens: 'the tour wraps up.',
            icon: 'grid',
            description:
                'This is a monitoring report, laid out just like the reports you have seen on ' +
                'pipelines: an executive summary first, then the metrics, then the drilldowns ' +
                'that show how they were calculated. Scroll through it to see where accuracy ' +
                'held up and where it slipped, and inspect the individual messages and ' +
                'predictions behind any number. That is how you go from "the line dipped" to ' +
                '"here is why". Click Continue to finish.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'next'],
        },
    ],
};
